<template>
  <div>
    <!-- Header-Action -->
    <div class="d-flex align-center mb-4">
      <h2 class="font-weight-light">Produkte verwalten</h2>
      <v-spacer></v-spacer>
      <v-btn color="primary" depressed rounded @click="openCreate">
        <v-icon left>mdi-plus</v-icon>
        Neues Produkt
      </v-btn>
    </div>

    <!-- Liste -->
    <div v-if="loading" class="text-center pa-12">
      <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
    </div>

    <div v-else-if="products.length === 0" class="text-center pa-12 grey--text">
      <v-icon size="64" color="grey darken-1">mdi-package-variant-remove</v-icon>
      <div class="mt-3">Noch keine Produkte erfasst</div>
    </div>

    <v-card v-else dark color="rgba(255,255,255,0.03)" flat>
      <v-data-table dark :headers="headers" :items="products" :items-per-page="50" class="transparent">
        <template v-slot:item.image="{ item }">
          <v-avatar size="40" tile rounded class="my-2">
            <admin-product-thumb :product="item" />
          </v-avatar>
        </template>
        <template v-slot:item.title="{ item }">
          <span class="font-weight-medium">{{ item.title }}</span>
        </template>
        <template v-slot:item.minMax="{ item }"> {{ item.minQuantity }} – {{ item.maxQuantity }} </template>
        <template v-slot:item.images="{ item }">
          <v-chip x-small dark color="blue-grey">
            <v-icon left x-small>mdi-image-multiple</v-icon>
            {{ item.images ? item.images.length : 0 }}
          </v-chip>
        </template>
        <template v-slot:item.studios="{ item }">
          <span v-if="!item.availableInStudios || item.availableInStudios.length === 0" class="caption grey--text"> Alle </span>
          <span v-else class="caption">{{ item.availableInStudios.length }} Studios</span>
        </template>
        <template v-slot:item.active="{ item }">
          <v-chip x-small :color="item.active ? 'green' : 'grey'" dark>
            {{ item.active ? "Aktiv" : "Inaktiv" }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon small @click="openEdit(item)">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small color="error" @click="confirmDelete(item)">
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog :fullscreen="$vuetify.breakpoint.smAndDown" v-model="dialogOpen" max-width="700" persistent scrollable>
      <v-card dark>
        <v-card-title>
          <v-icon left>{{ isEditMode ? "mdi-pencil" : "mdi-plus-circle" }}</v-icon>
          {{ isEditMode ? "Produkt bearbeiten" : "Neues Produkt" }}
        </v-card-title>

        <v-divider></v-divider>
        <v-card-text style="max-height: 70vh">
          <v-form ref="productForm" v-model="formValid" class="pt-3">
            <v-text-field v-model="form.title" label="Titel *" outlined dense dark :rules="[(v) => !!v || 'Pflichtfeld']" />

            <v-textarea v-model="form.description" label="Beschreibung" outlined dense dark rows="4" />

            <v-row dense>
              <v-col cols="6">
                <v-text-field v-model.number="form.minQuantity" label="Min. Anzahl" type="number" outlined dense dark min="1" />
              </v-col>
              <v-col cols="6">
                <v-text-field v-model.number="form.maxQuantity" label="Max. Anzahl" type="number" outlined dense dark min="1" />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  v-model.number="form.sortOrder"
                  label="Sortierung"
                  type="number"
                  outlined
                  dense
                  dark
                  hint="Kleinere Werte zuerst"
                  persistent-hint />
              </v-col>
              <v-col cols="6" class="d-flex align-center pl-3">
                <v-switch v-model="form.active" label="Aktiv" color="primary" hide-details inset />
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="form.availableInStudios"
                  :items="studioOptions"
                  item-text="name"
                  item-value="_id"
                  label="Verfügbar in Studios"
                  outlined
                  dense
                  dark
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  hint="Leer lassen = in allen Studios verfügbar"
                  persistent-hint
                  class="mt-2"
              /></v-col>
            </v-row>

            <!-- Bestehende Bilder -->
            <div v-if="isEditMode && existingImages.length > 0" class="mt-4">
              <div class="caption grey--text mb-2">Bestehende Bilder ({{ existingImages.length }})</div>
              <div class="existing-images">
                <div v-for="img in existingImages" :key="img.fileId" class="existing-img-wrap">
                  <img :src="imageUrls[img.fileId]" v-if="imageUrls[img.fileId]" class="existing-img" />
                  <div v-else class="existing-img-loading">
                    <v-progress-circular indeterminate size="20"></v-progress-circular>
                  </div>
                  <v-btn icon small class="remove-img-btn" color="error" @click="markImageForRemoval(img.fileId)">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>

            <!-- Neue Bilder -->
            <div class="mt-4">
              <v-file-input
                v-model="form.newImages"
                multiple
                accept="image/*"
                label="Bilder hochladen"
                outlined
                dense
                dark
                show-size
                counter
                prepend-icon=""
                prepend-inner-icon="mdi-image-plus"
                hint="Mehrere Bilder möglich. Max. 10 MB pro Bild."
                persistent-hint />
            </div>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog" :disabled="saving">Abbrechen</v-btn>
          <v-btn color="primary" depressed :loading="saving" :disabled="!formValid" @click="save">
            {{ isEditMode ? "Speichern" : "Erfassen" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"

// Inline-Subkomponente für Thumbnail mit lazy-loaded Bild
const AdminProductThumb = {
  name: "AdminProductThumb",

  props: { product: { type: Object, required: true } },
  data() {
    return { url: null }
  },
  async mounted() {
    const first = this.product.images && this.product.images[0]
    if (!first) return
    try {
      this.url = await api.marketingShop.loadProductImage(this.product._id, first.fileId)
    } catch (e) {
      /* ignore */
    }
  },
  beforeDestroy() {
    if (this.url) URL.revokeObjectURL(this.url)
  },
  template: `
    <div style="width: 100%; height: 100%;">
      <img v-if="url" :src="url" style="width: 100%; height: 100%; object-fit: cover;" />
      <div v-else style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.05);">
        <v-icon small color="grey darken-1">mdi-image-off</v-icon>
      </div>
    </div>`,
}

export default {
  name: "ProductAdmin",
  components: { AdminProductThumb },
  props: {
    products: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    studioOptions: { type: Array, default: () => [] }, // ← NEU
  },
  data() {
    return {
      dialogOpen: false,
      formValid: false,
      saving: false,
      editingId: null,
      form: this.getEmptyForm(),
      existingImages: [],
      imagesToRemove: [],
      imageUrls: {},
      headers: [
        { text: "Bild", value: "image", sortable: false, width: "60px" },
        { text: "Titel", value: "title" },
        { text: "Min/Max", value: "minMax", sortable: false },
        { text: "Bilder", value: "images", sortable: false },
        { text: "Sortierung", value: "sortOrder" },
        { text: "Status", value: "active" },
        { text: "Studios", value: "studios", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
    }
  },
  computed: {
    isEditMode() {
      return !!this.editingId
    },
  },
  methods: {
    getEmptyForm() {
      return {
        title: "",
        description: "",
        minQuantity: 1,
        maxQuantity: 100,
        sortOrder: 0,
        active: true,
        availableInStudios: [], // leer = alle Studios
        newImages: [],
      }
    },
    openCreate() {
      this.form = this.getEmptyForm()
      this.editingId = null
      this.existingImages = []
      this.imagesToRemove = []
      this.dialogOpen = true
      this.$nextTick(() => {
        if (this.$refs.productForm) this.$refs.productForm.resetValidation()
      })
    },
    async openEdit(product) {
      this.form = {
        title: product.title || "",
        description: product.description || "",
        minQuantity: product.minQuantity || 1,
        maxQuantity: product.maxQuantity || 100,
        sortOrder: product.sortOrder || 0,
        active: product.active !== false,
        availableInStudios: (product.availableInStudios || []).map((s) => (typeof s === "object" ? s._id : s)),
        newImages: [],
      }
      this.editingId = product._id
      this.existingImages = [...(product.images || [])]
      this.imagesToRemove = []
      this.imageUrls = {}
      this.dialogOpen = true

      // Bestehende Bilder laden
      for (const img of this.existingImages) {
        api.marketingShop
          .loadProductImage(product._id, img.fileId)
          .then((url) => this.$set(this.imageUrls, img.fileId, url))
          .catch(() => {
            /* ignore */
          })
      }
    },
    closeDialog() {
      this.dialogOpen = false
      this.editingId = null
      // Object-URLs aufräumen
      Object.values(this.imageUrls).forEach((u) => URL.revokeObjectURL(u))
      this.imageUrls = {}
    },
    markImageForRemoval(fileId) {
      this.imagesToRemove.push(fileId)
      this.existingImages = this.existingImages.filter((i) => i.fileId !== fileId)
      if (this.imageUrls[fileId]) {
        URL.revokeObjectURL(this.imageUrls[fileId])
        this.$delete(this.imageUrls, fileId)
      }
    },
    async save() {
      if (!this.$refs.productForm.validate()) return

      // Validation: maxQuantity >= minQuantity
      if (this.form.maxQuantity < this.form.minQuantity) {
        alert("Max. Anzahl muss grösser oder gleich Min. Anzahl sein")
        return
      }

      this.saving = true
      try {
        const fd = new FormData()
        fd.append("title", this.form.title.trim())
        fd.append("description", this.form.description || "")
        fd.append("minQuantity", this.form.minQuantity)
        fd.append("maxQuantity", this.form.maxQuantity)
        fd.append("sortOrder", this.form.sortOrder)
        fd.append("active", this.form.active)
        fd.append("availableInStudios", (this.form.availableInStudios || []).join(","))
        if (this.imagesToRemove.length > 0) {
          fd.append("removedImageIds", this.imagesToRemove.join(","))
        }
        if (this.form.newImages && this.form.newImages.length > 0) {
          this.form.newImages.forEach((f) => fd.append("images", f))
        }

        if (this.isEditMode) {
          await api.marketingShop.updateProduct(this.editingId, fd)
        } else {
          await api.marketingShop.createProduct(fd)
        }

        this.closeDialog()
        this.$emit("reload")
      } catch (e) {
        console.error("Speichern fehlgeschlagen:", e)
        const msg = e.response?.data?.error || "Fehler beim Speichern"
        alert(msg)
      } finally {
        this.saving = false
      }
    },
    async confirmDelete(product) {
      if (!confirm(`Produkt "${product.title}" wirklich löschen?`)) return
      try {
        await api.marketingShop.deleteProduct(product._id)
        this.$emit("reload")
      } catch (e) {
        console.error("Löschen fehlgeschlagen:", e)
        alert("Fehler beim Löschen")
      }
    },
  },
}
</script>

<style scoped>
.transparent {
  background: transparent !important;
}

.existing-images {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;
}

.existing-img-wrap {
  position: relative;
  aspect-ratio: 1;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 6px;
  overflow: hidden;
}

.existing-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.existing-img-loading {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-img-btn {
  position: absolute !important;
  top: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.6) !important;
}
</style>
